import gql from "graphql-tag";
import { useQuery } from "@vue/apollo-composable";
import type { Ref } from "@vue/composition-api";

type Vars = Record<string, unknown>;
type VarsInput = Vars | Ref<Vars> | (() => Vars);

function useDetailQuery<TResult>(
  document: ReturnType<typeof gql>,
  variables: VarsInput = {},
  options: Record<string, unknown> = {}
) {
  return useQuery<TResult, Vars>(document, variables as any, options as any);
}

const userRef = `
  createdBy {
    Id
    Username
  }
`;

const attachmentRef = `
  attachments {
    Id
    Name
  }
`;

export const CompanyDetailDocument = gql`
  query CompanyDetail($id: String!) {
    company(id: $id) {
      Id
      Name
      Address
      Focus
      Webpage
      Language
      UpdatedAt
      CreatedAt
      ${userRef}
      contact {
        Id
      }
    }
  }
`;

export const CompanyPilverDetailDocument = gql`
  query CompanyPilverDetail($id: String!) {
    companyPilver(id: $id) {
      Id
      Name
      Address
      Focus
      Webpage
      Language
      UpdatedAt
      CreatedAt
      ${userRef}
      contact {
        Id
      }
    }
  }
`;

export const ContactDetailDocument = gql`
  query ContactDetail($id: String!) {
    contact(id: $id) {
      Id
      ContactPerson
      Phone
      Email
      Interest
      Description
      CreatedAt
      UpdatedAt
      company {
        Id
        Name
      }
      contactedBy {
        Id
        Username
      }
    }
  }
`;

export const ContactPilverDetailDocument = gql`
  query ContactPilverDetail($id: String!) {
    contactPilver(id: $id) {
      Id
      ContactPerson
      Phone
      Email
      Interest
      Description
      CreatedAt
      UpdatedAt
      company {
        Id
        Name
      }
      contactedBy {
        Id
        Username
      }
    }
  }
`;

export const ProjectDetailDocument = gql`
  query ProjectDetail($id: String!) {
    project(id: $id) {
      Id
      Name
      Address
      Type
      Language
      Manager
      ManagerContact
      UpdatedAt
      CreatedAt
      ${userRef}
      projectChange {
        Id
      }
    }
  }
`;

export const ProjectChangeDetailDocument = gql`
  query ProjectChangeDetail($id: String!) {
    projectChange(id: $id) {
      Id
      State
      Description
      CreatedAt
      UpdatedAt
      project {
        Id
        Name
      }
      modifiedBy {
        Id
        Username
      }
    }
  }
`;

export const WorkerDetailDocument = gql`
  query WorkerDetail($id: String!) {
    worker(id: $id) {
      Id
      Name
      Email
      Phone
      Type
      Profession
      Note
      UpdatedAt
      CreatedAt
      ${userRef}
      ${attachmentRef}
    }
  }
`;

export const MiddlemanDetailDocument = gql`
  query MiddlemanDetail($id: String!) {
    middleman(id: $id) {
      Id
      Name
      Email
      Phone
      Note
      UpdatedAt
      CreatedAt
      ${userRef}
      ${attachmentRef}
    }
  }
`;

export const UserDetailDocument = gql`
  query UserDetail($id: String!) {
    user(id: $id) {
      Id
      Username
      Email
      Role
      Status
      CreatedAt
      UpdatedAt
    }
  }
`;

export const MeDocument = gql`
  query MeDetail {
    me {
      Id
      Username
      Email
      Role
      Status
      UpdatedAt
    }
  }
`;

export function useCompanyDetailQuery(variables: VarsInput = {}) {
  return useDetailQuery<{ company: any }>(CompanyDetailDocument, variables);
}

export function useCompanyPilverDetailQuery(variables: VarsInput = {}) {
  return useDetailQuery<{ companyPilver: any }>(
    CompanyPilverDetailDocument,
    variables
  );
}

export function useContactDetailQuery(
  variables: VarsInput = {},
  options: Record<string, unknown> = {}
) {
  return useDetailQuery<{ contact: any }>(
    ContactDetailDocument,
    variables,
    options
  );
}

export function useContactPilverDetailQuery(
  variables: VarsInput = {},
  options: Record<string, unknown> = {}
) {
  return useDetailQuery<{ contactPilver: any }>(
    ContactPilverDetailDocument,
    variables,
    options
  );
}

export function useProjectDetailQuery(variables: VarsInput = {}) {
  return useDetailQuery<{ project: any }>(ProjectDetailDocument, variables);
}

export function useProjectChangeDetailQuery(
  variables: VarsInput = {},
  options: Record<string, unknown> = {}
) {
  return useDetailQuery<{ projectChange: any }>(
    ProjectChangeDetailDocument,
    variables,
    options
  );
}

export function useWorkerDetailQuery(variables: VarsInput = {}) {
  return useDetailQuery<{ worker: any }>(WorkerDetailDocument, variables);
}

export function useMiddlemanDetailQuery(variables: VarsInput = {}) {
  return useDetailQuery<{ middleman: any }>(MiddlemanDetailDocument, variables);
}

export function useUserDetailQuery(variables: VarsInput = {}) {
  return useDetailQuery<{ user: any }>(UserDetailDocument, variables);
}

export function useMeDetailQuery(
  variables: VarsInput = {},
  options: Record<string, unknown> = {}
) {
  return useDetailQuery<{ me: any }>(MeDocument, variables, options);
}
